import { useState } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { Shield, LayoutDashboard, Users, Calendar, ClipboardCheck, BarChart3, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OverviewPage from "@/pages/dashboard/OverviewPage";
import PlayersPage from "@/pages/dashboard/PlayersPage";
import SessionsPage from "@/pages/dashboard/SessionsPage";
import AttendancePage from "@/pages/dashboard/AttendancePage";
import PerformancePage from "@/pages/dashboard/PerformancePage";

const navItems = [
  { href: "/dashboard", label: "Overview", icon: LayoutDashboard, exact: true },
  { href: "/dashboard/players", label: "Players", icon: Users },
  { href: "/dashboard/sessions", label: "Sessions", icon: Calendar },
  { href: "/dashboard/attendance", label: "Attendance", icon: ClipboardCheck },
  { href: "/dashboard/performance", label: "Performance", icon: BarChart3 },
];

function Sidebar({ mobile, onClose }) {
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (item) =>
    item.exact ? location.pathname === item.href : location.pathname.startsWith(item.href);

  return (
    <div className={`flex flex-col h-full bg-gray-900 ${mobile ? "w-72" : "w-64"}`}>
      <div className="flex items-center gap-2 px-6 py-5 border-b border-gray-800">
        <div className="w-8 h-8 bg-green-600 rounded-sm flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <span
          className="text-lg font-black text-white"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          NextTackle
        </span>
        {mobile && (
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={mobile ? onClose : undefined}
            data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              isActive(item)
                ? "bg-green-600 text-white"
                : "text-gray-400 hover:text-white hover:bg-gray-800"
            }`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-gray-800">
        <div className="flex items-center gap-3 px-3 py-2 mb-1">
          <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {user?.name?.charAt(0) || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">{user?.name}</div>
            <div className="text-gray-400 text-xs truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="dashboard-logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden" data-testid="dashboard-layout">
      <div className="hidden md:flex flex-shrink-0">
        <Sidebar />
      </div>

      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="relative z-10">
            <Sidebar mobile onClose={() => setMobileSidebarOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            data-testid="mobile-sidebar-toggle"
            className="p-1.5 text-gray-600"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-600 rounded-sm flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span
              className="font-black text-gray-900 text-sm"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              NextTackle
            </span>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route index element={<OverviewPage />} />
            <Route path="players" element={<PlayersPage />} />
            <Route path="sessions" element={<SessionsPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="performance" element={<PerformancePage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
